import { put, select, call, take, fork, all } from 'redux-saga/effects'
import { getFormValues } from 'redux-form'
import { flatten } from 'ramda/es'

import { snackbarActions } from 'reduxModules/actions/SnackbarActions'
import { routeActions } from 'reduxModules/actions/RouteActions'
import { bookingTypes } from 'reduxModules/actions/BookingActions'
import { modalActions } from 'reduxModules/actions/ModalActions'
import { mergedSourcesSelector } from 'reduxModules/selectors/SourceSelectors'
import {
  routeIdSelector,
  limitSelector,
  routesSelector,
} from 'reduxModules/selectors/RouteSelectors'
import { dateSelector } from 'reduxModules/selectors/DateSelectors'
import { isPartnerSelector, userIdSelector, isModultransPartnerSelector } from 'reduxModules/selectors/UserSelectors'
import {
  createReadableDate,
  addDays,
  createDateFromDateId,
  sortBy,
  createReturnRouteId,
  handlePhoneNumber
} from 'services/helpers'
import {
  confirmSaga,
  handleSourceData,
  handleRouteLimit,
  removeBookedPlace,
  addBookedPlace,
  updateTotalSheetBookings
} from 'services/sagaHelpers'
import { allCities } from 'constants/arrays'

import { firebase, firestore, rsf } from '../../firebase'

function* unsubscribeDepartureBookingsSaga(unsubscribe) {
  while (true) {
    yield take(bookingTypes.UNSUBSCRIBE_DEPARTURE_BOOKINGS_ATTEMPT)
    unsubscribe.close()
  }
}

export function* subscribeDepartureBookingsSaga(action) {
  const { responseSuccess, responseFailure, data: routeId } = action

  try {
    const date = yield select(dateSelector)
    const updatedDate = addDays(date, -2)
    const departureDate = createReadableDate(updatedDate)
    const reverseRouteId = createReturnRouteId(routeId)

    const colRef = yield firestore.collection('bookings')
    const query = yield colRef
      .where('departureDate', '==', departureDate)
      .where('routeId', '==', reverseRouteId)

    const channel = yield rsf.firestore.channel(query)

    yield fork(unsubscribeDepartureBookingsSaga, channel)

    while (true) {
      const res = yield take(channel)

      if (!res.empty) {
        const bookings = []

        res.forEach(booking => bookings.push({ id: booking.id, ...booking.data() }))

        yield put(responseSuccess(bookings))
      } else {
        yield put(responseSuccess([]))
      }
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час завантаження бронювань'
    }))
  }
}

function* unsubscribeBookinsSaga(unsubscribe) {
  while (true) {
    yield take(bookingTypes.UNSUBSCRIBE_BOOKINGS_ATTEMPT)
    unsubscribe.close()
  }
}

export function* subscribeBookingsSaga(action) {
  const { responseSuccess, responseFailure } = action

  try {
    const date = yield select(dateSelector)
    const departureDate = createReadableDate(date)
    const routeId = yield select(routeIdSelector)
    const isPartner = yield select(isPartnerSelector)
    const userId = yield select(userIdSelector)
    const isModultransPartner = yield select(isModultransPartnerSelector)

    const colRef = yield firestore.collection('bookings')
    const query = isPartner && !isModultransPartner
      ? yield colRef
        .where('departureDate', '==', departureDate)
        .where('routeId', '==', routeId)
        .where('userId', '==', userId)
      : yield colRef
        .where('departureDate', '==', departureDate)
        .where('routeId', '==', routeId)

    const channel = yield rsf.firestore.channel(query)

    yield fork(unsubscribeBookinsSaga, channel)

    while (true) {
      const res = yield take(channel)

      if (!res.empty) {
        const bookings = []

        res.forEach(booking => bookings.push({ id: booking.id, ...booking.data() }))

        yield put(responseSuccess(bookings))
      } else {
        yield put(responseSuccess([]))
      }
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час завантаження бронювань'
    }))
  }
}

function* unsubscribeWebsiteBookinsSaga(unsubscribe) {
  while (true) {
    yield take(bookingTypes.UNSUBSCRIBE_WEBSITE_BOOKINGS_ATTEMPT)
    unsubscribe.close()
  }
}

export function* subscribeWebsiteBookingsSaga(action) {
  const { responseSuccess, responseFailure } = action

  try {
    let nextMonth = new Date()
    let date = yield select(dateSelector)

    date.setDate(1)
    nextMonth.setDate(1)
    nextMonth.setMonth(date.getMonth() + 1)

    const colRef = yield firestore.collection('bookings')
    const query = yield colRef
      .where('sourceId', '==', 'new_website')
      .where('createdDateTimestamp', '>=', date.valueOf())
      .where('createdDateTimestamp', '<=', nextMonth.valueOf())

    const channel = yield rsf.firestore.channel(query)

    yield fork(unsubscribeWebsiteBookinsSaga, channel)

    while (true) {
      const res = yield take(channel)

      if (!res.empty) {
        const bookings = []

        res.forEach(booking => bookings.push({ id: booking.id, ...booking.data() }))

        yield put(responseSuccess(bookings))
      } else {
        yield put(responseSuccess([]))
      }
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час завантаження бронювань'
    }))
  }
}

function* unsubscribeReturnBookingsSaga(unsubscribe) {
  while (true) {
    yield take(bookingTypes.UNSUBSCRIBE_RETURN_BOOKINGS_ATTEMPT)
    unsubscribe.close()
  }
}

export function* subscribeReturnBookingsSaga(action) {
  const { responseSuccess, responseFailure, data: returnRouteId } = action

  try {
    const date = yield select(dateSelector)
    const updatedDate = addDays(date, 2)
    const returnDate = createReadableDate(updatedDate)

    const colRef = yield firestore.collection('bookings')
    const query = yield colRef.where('departureDate', '==', returnDate).where('routeId', '==', returnRouteId)

    const channel = yield rsf.firestore.channel(query)

    yield fork(unsubscribeReturnBookingsSaga, channel)

    while (true) {
      const res = yield take(channel)

      if (!res.empty) {
        const bookings = []

        res.forEach(booking => bookings.push({ id: booking.id, ...booking.data() }))

        const sortedBookings = sortBy(bookings, allCities, 'from', 'note', 'to')

        yield put(responseSuccess(sortedBookings.reverse()))
      } else {
        yield put(responseSuccess([]))
      }
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час завантаження зворотніх бронювань'
    }))
  }
}

export function* setBookingSaga(action) {
  const { responseSuccess, responseFailure } = action

  try {
    const data = yield select(getFormValues('booking'))

    yield call(rsf.firestore.setDocument, `bookings/${data.id}`, data, { merge: true })

    yield put(snackbarActions.showSnackbar({
      type: 'success',
      message: 'Бронювання успішно зміненно!'
    }))
    yield put(responseSuccess())
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час зміни бронювання!'
    }))
  }
}

export function* deleteBookingSaga(action) {
  const { responseSuccess, responseFailure, data: booking } = action

  try {
    const { id, source, sourceId } = booking
    const confirmed = yield call(confirmSaga, 'deleteBooking')
    const phoneNumber = handlePhoneNumber(booking.phone)

    if (confirmed) {
      yield call(rsf.firestore.deleteDocument, `bookings/${id}`)

      yield put(snackbarActions.showSnackbar({
        type: 'success',
        message: 'Бронювання успішно видалено!'
      }))

      yield removeBookedPlace(booking.place)

      yield call(rsf.firestore.setDocument,
        `passengers/${phoneNumber}`,
        { bookingsIDs: firebase.firestore.FieldValue.arrayRemove(id) },
        { merge: true }
      )

      if (source === 'system') {
        const mergedSources = yield select(mergedSourcesSelector)

        const updatedSources = mergedSources.map(source => {
          if (source.id === sourceId) {
            return {
              ...source,
              booked: source.booked - 1
            }
          } else {
            return source
          }
        })

        yield put(routeActions.changeSourceData(updatedSources))

        yield handleSourceData()
      } else {
        const limit = yield select(limitSelector)

        yield handleRouteLimit(+limit + 1)
        yield updateTotalSheetBookings()
      }

      yield put(responseSuccess())
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час видалення бронювання!'
    }))
  }
}

export function* setBookingStatusSaga(action) {
  const { responseSuccess, responseFailure, data: { id, status, type } } = action

  try {
    const cancelConfirm = status === 'confirmed' && type === 'confirm'
    const cancelResponce = status === 'noResponse' && type === 'response'

    if (cancelConfirm || cancelResponce) {
      yield call(rsf.firestore.setDocument, `bookings/${id}`, { status: '' }, { merge: true })
    } else if (type === 'confirm') {
      yield call(rsf.firestore.setDocument, `bookings/${id}`, { status: 'confirmed' }, { merge: true })
    } else if (type === 'response') {
      yield call(rsf.firestore.setDocument, `bookings/${id}`, { status: 'noResponse' }, { merge: true })
    }

    yield put(responseSuccess())
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час зміни статуса бронювання!'
    }))
  }
}

export function* setPassengerPlaceSaga(action) {
  const { responseSuccess, responseFailure, data: { id, value, prevValue } } = action

  try {
    yield call(
      rsf.firestore.setDocument,
      `bookings/${id}`,
      { place: value },
      { merge: true }
    )

    yield removeBookedPlace(prevValue)
    yield addBookedPlace(value)

    yield put(responseSuccess())
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час зміни місця бронювання!'
    }))
  }
}

export function* getRangeBookingsSaga(action) {
  const { responseSuccess, responseFailure, data: { fromDate, toDate } } = action

  const updatedDateFrom = addDays(new Date(fromDate), -1).valueOf();
  const updatedDateTo = new Date(toDate).valueOf();

  try {
    const userId = yield select(userIdSelector)

    const colRef = yield firestore.collection('bookings')
    const query = yield colRef
      .where('userId', '==', userId)
      .where('departureTimestamp', '>=', updatedDateFrom)
      .where('departureTimestamp', '<=', updatedDateTo)

    const res = yield call(rsf.firestore.getCollection, query)

    if (res.empty) {
      yield put(responseSuccess())
      yield put(snackbarActions.showSnackbar({
        type: 'error',
        message: 'На заданий період часу бронювання відсутні'
      }))
    } else {
      const bookings = []

      res.forEach(booking => bookings.push({ id: booking.id, ...booking.data() }))

      if (status === 'paid') {
        const sortedBookings = bookings.filter(booking => booking.status === status)

        yield put(modalActions.showModal({
          type: 'printRangeBookings',
          data: { bookings: sortedBookings, convert: true }
        }))
        yield put(responseSuccess())
      } else {
        yield put(modalActions.showModal({ type: 'printRangeBookings', data: { bookings, convert: true } }))
        yield put(responseSuccess())
      }
    }
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час завантаження бронювань'
    }))
  }
}

export function* getCarriersBookingsSaga(action) {
  const { responseSuccess, responseFailure, data: carrierId } = action

  try {
    const routes = yield select(routesSelector)
    const date = yield select(dateSelector)

    const month = date.getMonth() + 1
    const year = date.getFullYear()

    const carriersRoutesInfo = yield all(
      routes.map(route => {
        const colRef = firestore.collection(`routes/${route.id}/info`)
        const query = colRef
          .where('carrier', '==', carrierId)
          .where('month', '==', month)
          .where('year', '==', year)

        return call(rsf.firestore.getCollection, query)
      })
    )

    const carriersRoutesDates = carriersRoutesInfo.map(info => {
      const routeDates = []

      info.forEach(info => {
        const routeId = info.ref.parent.parent.id
        const date = createDateFromDateId(info.id)

        routeDates.push({ routeId, timestamp: date.valueOf() })
      })

      return routeDates
    })

    const carriersBookings = yield all(
      flatten(carriersRoutesDates).map(({ timestamp, routeId }) => {
        const colRef = firestore.collection('bookings')
        const query = colRef
          .where('departureTimestamp', '==', timestamp)
          .where('routeId', '==', routeId)

        return call(rsf.firestore.getCollection, query)
      })
    )

    const allBookings = carriersBookings.map(snapshot => {
      const bookings = []

      snapshot.forEach(data => bookings.push({ id: data.id, ...data.data() }))

      return bookings
    })

    const bookings = flatten(allBookings).filter(({ source }) => source !== 'partner' && source !== 'system')

    yield put(modalActions.showModal({ type: 'printRangeBookings', data: { bookings } }))
    yield put(responseSuccess())
  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час завантаження бронювань перевізника'
    }))
  }
}

export function* getUserBookingsSaga(action) {
  const { responseSuccess, responseFailure, data: userId } = action

  try {
    const date = yield select(dateSelector)

    const month = date.getMonth() + 1
    const year = date.getFullYear()

    const dateFrom = new Date(year, month - 1);
    const dateTo = new Date(year, month);

    const colRef = yield firestore.collection('bookings')
    const query = yield colRef
      .where('userId', '==', userId)
      .where('departureTimestamp', '>=', dateFrom.valueOf())
      .where('departureTimestamp', '<', dateTo.valueOf())

    const res = yield call(rsf.firestore.getCollection, query)

    if (!res.empty) {
      const allBookings = []

      res.forEach(booking => allBookings.push({ id: booking.id, ...booking.data() }))

      const bookings = allBookings.filter(({ source }) => source !== 'system')

      yield put(modalActions.showModal({ type: 'printRangeBookings', data: { bookings } }))
      yield put(responseSuccess())
    } else {
      yield put(responseSuccess())
      yield put(snackbarActions.showSnackbar({
        type: 'error',
        message: 'Відсутні бронювання для даного користувача'
      }))
    }

  } catch (err) {
    console.log(err)
    yield put(responseFailure())
    yield put(snackbarActions.showSnackbar({
      type: 'error',
      message: 'Сталась помилка під час завантаження бронювань користувача'
    }))
  }
}



// WEBPACK FOOTER //
// ./src/reduxModules/sagas/BookingSagas.js